import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { getFeaturedProducts, COLLECTION_NOTES, HERO_IMAGE } from '../data/products';
import { useCart } from '../context/CartContext';
import Header from '../components/Header';

const MemberHome = () => {
  const navigate = useNavigate();
  const { itemCount, setIsOpen } = useCart();
  const featuredProducts = getFeaturedProducts();

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background */}
        <motion.div
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.5, ease: "easeOut" }}
          className="absolute inset-0"
        >
          <img
            src={HERO_IMAGE}
            alt="Secret 13 Collection"
            className="w-full h-full object-cover opacity-30"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/50 to-transparent" />

        {/* Hero Content */}
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          {/* Brand Name */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light text-zinc-50 mb-6"
          >
            Secret 13
          </motion.h1>

          {/* Brand Tagline */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="font-serif text-xl sm:text-2xl lg:text-3xl font-light text-zinc-300 mb-4 italic"
          >
            A Collection of Secrets
          </motion.p>

          {/* Brand Values */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.7 }}
            className="font-sans text-sm sm:text-base text-zinc-500 tracking-widest uppercase mb-8"
          >
            Private. Curated. Limited.
          </motion.p>

          {/* Supporting Line */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.9 }}
            className="font-sans text-sm text-zinc-600 mb-12 max-w-md mx-auto"
          >
            Each piece is crafted as an experience, not a product.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1 }}
          >
            <button
              data-testid="explore-button"
              onClick={() => document.getElementById('featured')?.scrollIntoView({ behavior: 'smooth' })}
              className="inline-flex items-center gap-3 border border-zinc-700 text-white px-8 py-4 font-sans text-xs font-medium tracking-[0.2em] uppercase hover:border-white transition-all duration-300"
            >
              Discover the Secrets
              <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.4 }}
          className="absolute bottom-12 left-1/2 -translate-x-1/2"
        >
          <div className="w-px h-16 bg-gradient-to-b from-transparent via-zinc-600 to-transparent animate-pulse-subtle" />
        </motion.div>
      </section>

      {/* Featured Secrets Section */}
      <section id="featured" className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="font-sans text-xs tracking-[0.3em] uppercase text-zinc-500 mb-4">
              Featured
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-zinc-50 mb-4">
              The Core Secrets
            </h2>
            <p className="font-sans text-sm text-zinc-600 max-w-md mx-auto">
              Three essential experiences. Each one a world of its own.
            </p>
          </motion.div>

          {/* Products Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {featuredProducts.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
              >
                <div
                  data-testid={`product-card-${product.id}`}
                  onClick={() => navigate(`/product/${product.id}`)}
                  className="group cursor-pointer"
                >
                  <div className="relative aspect-[3/4] bg-[#0A0A0A] border border-white/5 overflow-hidden mb-6 group-hover:border-white/20 transition-all duration-500">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </div>
                  <p className="font-sans text-xs tracking-[0.2em] uppercase text-zinc-500 mb-2">
                    {product.secretName}
                  </p>
                  <h3 className="font-serif text-xl text-zinc-50 group-hover:text-white transition-colors mb-2">
                    {product.name}
                  </h3>
                  <p className="font-sans text-xs text-zinc-600 italic">
                    {product.tagline}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* View All Button */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-center mt-16"
          >
            <button
              data-testid="view-all-button"
              onClick={() => navigate('/collection/all')}
              className="inline-flex items-center gap-3 text-zinc-400 hover:text-white font-sans text-xs tracking-[0.2em] uppercase transition-colors duration-300"
            >
              View All Secrets
              <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Collection Notes Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="font-sans text-xs tracking-[0.3em] uppercase text-zinc-500 mb-4">
              Categories
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-zinc-50 mb-4">
              Types of Secrets
            </h2>
            <p className="font-sans text-sm text-zinc-600 max-w-md mx-auto">
              Different moments call for different experiences.
            </p>
          </motion.div>

          {/* Notes Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {COLLECTION_NOTES.map((note, index) => (
              <motion.div
                key={note.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
              >
                <div
                  data-testid={`collection-note-${note.id}`}
                  onClick={() => navigate(`/collection/${note.id}`)}
                  className="collection-card relative aspect-square cursor-pointer overflow-hidden"
                >
                  <img
                    src={note.image}
                    alt={note.name}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 z-10 flex flex-col items-center justify-end p-6 text-center">
                    <h3 className="font-serif text-xl sm:text-2xl text-zinc-50 mb-1">
                      {note.name}
                    </h3>
                    <p className="font-sans text-xs text-zinc-400 hidden sm:block">
                      {note.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Philosophy Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="w-px h-16 bg-gradient-to-b from-transparent via-zinc-700 to-transparent mx-auto mb-12" />
            
            <p className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light text-zinc-300 leading-relaxed mb-6">
              "A secret is not something hidden.
            </p>
            <p className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light text-zinc-300 leading-relaxed mb-12">
              It is something shared only with those who understand."
            </p>
            
            <div className="w-12 h-px bg-zinc-700 mx-auto mb-8" />
            
            <p className="font-sans text-xs tracking-[0.3em] uppercase text-zinc-600">
              Secret 13
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <p className="font-serif text-xl text-zinc-50">Secret 13</p>
          <p className="font-sans text-xs text-zinc-600">
            © 2025 Secret 13. All rights reserved. Members only.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default MemberHome;
